import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import ContactSearchModal from './ContactSearchModal';
import ObituaryAttachments from './ObituaryAttachments';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  UserIcon,
  XMarkIcon,
  LinkIcon,
  EyeIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';

const ObituaryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AppContext);
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    maidenName: '',
    suffix: '',
    dateOfBirth: '',
    dateOfDeath: '',
    biography: '',
    birthplace: '',
    residence: '',
    serviceDate: '',
    serviceLocation: '',
    serviceNotes: '',
    condolencesAddress: '',
    memorialDonations: '',
    isPrivate: false,
    contactId: null
  });

  const [linkedContact, setLinkedContact] = useState(null);
  const [obituaryData, setObituaryData] = useState(null);
  const [error, setError] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (!isNew) {
      fetchObituary();
    }
  }, [id]);

  const fetchObituary = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/obituaries/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setObituaryData(data);
        setFormData({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          middleName: data.middleName || '',
          maidenName: data.maidenName || '',
          suffix: data.suffix || '',
          dateOfBirth: data.dateOfBirth || '',
          dateOfDeath: data.dateOfDeath || '',
          biography: data.biography || '',
          birthplace: data.birthplace || '',
          residence: data.residence || '',
          serviceDate: data.serviceDate ? data.serviceDate.substring(0, 16) : '',
          serviceLocation: data.serviceLocation || '',
          serviceNotes: data.serviceNotes || '',
          condolencesAddress: data.condolencesAddress || '',
          memorialDonations: data.memorialDonations || '',
          isPrivate: data.isPrivate || false,
          contactId: data.contactId
        });
        if (data.contact) {
          setLinkedContact(data.contact);
        }
      } else {
        navigate('/obituaries');
      }
    } catch (error) {
      console.error('Error fetching obituary:', error);
      navigate('/obituaries');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async (publish = false) => {
    try {
      setSaving(true);
      setError(null);

      const payload = {
        ...formData,
        contactId: formData.contactId || null
      };

      let response;
      if (isNew) {
        response = await fetch('/api/obituaries', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`/api/obituaries/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        const savedObituary = await response.json();

        if (publish) {
          await fetch(`/api/obituaries/${savedObituary.id}/publish`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}` }
          });
        }

        if (isNew) {
          navigate(`/obituaries/${savedObituary.id}`);
        } else {
          fetchObituary();
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to save obituary');
      }
    } catch (err) {
      console.error('Error saving obituary:', err);
      setError('An unexpected error occurred while saving');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (isNew) {
      await handleSave(true);
    } else {
      try {
        await handleSave();
        const response = await fetch(`/api/obituaries/${id}/publish`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          fetchObituary();
        }
      } catch (error) {
        console.error('Error publishing obituary:', error);
      }
    }
  };

  const handleUnpublish = async () => {
    try {
      const response = await fetch(`/api/obituaries/${id}/unpublish`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        fetchObituary();
      }
    } catch (error) {
      console.error('Error unpublishing obituary:', error);
    }
  };

  const handleContactSelect = (contact) => {
    setFormData(prev => ({ ...prev, contactId: contact.id }));
    setLinkedContact(contact);
    setShowContactModal(false);
  };

  const handleRemoveContact = () => {
    setFormData(prev => ({ ...prev, contactId: null }));
    setLinkedContact(null);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/obituaries')}
            className="text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {isNew ? 'Create Obituary' : 'Edit Obituary'}
            </h1>
            {obituaryData && (
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  obituaryData.status === 'published'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {obituaryData.status === 'published' ? 'Published' : 'Draft'}
                </span>
                {obituaryData.isPrivate && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Private
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {obituaryData?.status === 'published' && !obituaryData?.isPrivate && obituaryData?.publicSlug && (
            <a
              href={`/public?obituary=${obituaryData.publicSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <EyeIcon className="h-5 w-5 mr-2" />
              View Public
            </a>
          )}
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            <DocumentTextIcon className="h-5 w-5 mr-2" />
            {saving ? 'Saving...' : (obituaryData?.status === 'published' ? 'Save' : 'Save Draft')}
          </button>
          {obituaryData?.status === 'published' ? (
            <button
              onClick={handleUnpublish}
              className="inline-flex items-center px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors"
            >
              Unpublish
            </button>
          ) : (
            <button
              onClick={handlePublish}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 bg-brand-green-600 text-white rounded-lg hover:bg-brand-green-700 transition-colors disabled:opacity-50"
            >
              <CheckCircleIcon className="h-5 w-5 mr-2" />
              {isNew ? 'Save & Publish' : 'Publish'}
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <XMarkIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5 cursor-pointer" onClick={() => setError(null)} />
          <div>
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Middle Name
                </label>
                <input
                  type="text"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Suffix
                </label>
                <input
                  type="text"
                  name="suffix"
                  value={formData.suffix}
                  onChange={handleChange}
                  placeholder="Jr., Sr., III, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maiden Name
                </label>
                <input
                  type="text"
                  name="maidenName"
                  value={formData.maidenName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Death
                  </label>
                  <input
                    type="date"
                    name="dateOfDeath"
                    value={formData.dateOfDeath}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Birthplace
                </label>
                <input
                  type="text"
                  name="birthplace"
                  value={formData.birthplace}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Residence
                </label>
                <input
                  type="text"
                  name="residence"
                  value={formData.residence}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Biography */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Obituary Text</h2>
            <textarea
              name="biography"
              value={formData.biography}
              onChange={handleChange}
              rows={10}
              placeholder="Write the obituary text here..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
            />
          </div>

          {/* Service Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Service Information</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    name="serviceDate"
                    value={formData.serviceDate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Location
                  </label>
                  <input
                    type="text"
                    name="serviceLocation"
                    value={formData.serviceLocation}
                    onChange={handleChange}
                    placeholder="Church, funeral home, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Notes
                </label>
                <textarea
                  name="serviceNotes"
                  value={formData.serviceNotes}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Visitation hours, reception details, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Memorial Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Memorial Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Condolences Address
                </label>
                <textarea
                  name="condolencesAddress"
                  value={formData.condolencesAddress}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Where to send cards, flowers, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Memorial Donations
                </label>
                <textarea
                  name="memorialDonations"
                  value={formData.memorialDonations}
                  onChange={handleChange}
                  rows={2}
                  placeholder="In lieu of flowers, donations may be made to..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Attachments */}
          {!isNew && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Photos & Documents</h2>
              <ObituaryAttachments obituaryId={id} token={token} />
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Settings</h2>
            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="isPrivate"
                  checked={formData.isPrivate}
                  onChange={handleChange}
                  className="h-4 w-4 text-brand-green-600 focus:ring-brand-green-500 border-gray-300 rounded"
                />
                <div>
                  <span className="text-sm font-medium text-gray-700">Private Obituary</span>
                  <p className="text-xs text-gray-500">
                    When enabled, this obituary will not appear on the public portal even when published.
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Linked Contact */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Linked Contact</h2>
            {linkedContact ? (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-brand-green-100 flex items-center justify-center">
                    <UserIcon className="h-5 w-5 text-brand-green-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {linkedContact.firstName} {linkedContact.lastName}
                    </div>
                    <div className="text-xs text-gray-500">
                      Contact #{linkedContact.id}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleRemoveContact}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowContactModal(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-brand-green-400 hover:text-brand-green-600 transition-colors"
              >
                <LinkIcon className="h-5 w-5" />
                Link to Contact
              </button>
            )}
            <p className="mt-3 text-xs text-gray-500">
              Linking to a contact allows the obituary to appear on plot details when the contact is interred.
            </p>
          </div>

          {/* Public Link */}
          {obituaryData?.status === 'published' && !obituaryData?.isPrivate && obituaryData?.publicSlug && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Public Link</h2>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-2">Share this link:</p>
                <code className="text-sm text-brand-green-600 break-all">
                  {window.location.origin}/public?obituary={obituaryData.publicSlug}
                </code>
              </div>
              <button
                onClick={() => {
                  const link = `${window.location.origin}/public?obituary=${obituaryData.publicSlug}`;
                  navigator.clipboard.writeText(link).then(() => {
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 2000);
                  });
                }}
                className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {linkCopied ? (
                  <>
                    <ClipboardDocumentCheckIcon className="h-5 w-5 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className="h-5 w-5" />
                    Copy Link
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Contact Search Modal */}
      {showContactModal && (
        <ContactSearchModal
          open={true}
          onSelect={handleContactSelect}
          onClose={() => setShowContactModal(false)}
          token={token}
        />
      )}
    </div>
  );
};

export default ObituaryForm;
